/**
 * Demo data sanitizer — replaces real tenant names and empty/integer
 * report versions with deterministic placeholder values when demo mode
 * is active.
 *
 * Operates at the API response layer (see api/client.ts) so every
 * tenantName/reportVersion that flows through the app is sanitized in
 * one place — no per-component changes needed.
 *
 * Why this exists:
 *   - On the Vercel deployment the real backend returns real tenant names
 *     from the legacy DB. Showing those during a client demo is bad.
 *   - Some report records also come back with empty `reportVersion`,
 *     rendering as just "v" in the UI. Demo mode generates a sensible
 *     X.Y version for these.
 *
 * Determinism:
 *   - The same real tenant name always maps to the same NATO placeholder
 *     within a session, so a tenant referenced on multiple pages stays
 *     consistent.
 *   - Report versions are derived from the report ID, so the same report
 *     always shows the same generated version.
 */

import { isDemoMode } from './demo-mode';

// ---------------------------------------------------------------------------
// Tenant-name placeholder pool
// ---------------------------------------------------------------------------

const NATO = [
  'Alpha', 'Bravo', 'Charlie', 'Delta', 'Echo', 'Foxtrot',
  'Golf', 'Hotel', 'India', 'Juliet', 'Kilo', 'Lima',
  'Mike', 'November', 'Oscar', 'Papa', 'Quebec', 'Romeo',
  'Sierra', 'Tango', 'Uniform', 'Victor', 'Whiskey', 'Xray',
  'Yankee', 'Zulu',
] as const;

const SUFFIXES = [
  'Holdings', 'Industries', 'Capital', 'LLC', 'Group',
  'Partners', 'Tech', 'Properties', 'Corp', 'Inc',
] as const;

const tenantNameMap = new Map<string, string>();

/** djb2-style hash → stable across sessions for the same input */
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function fakeTenantName(real: string): string {
  const cached = tenantNameMap.get(real);
  if (cached) return cached;

  const h = hash(real);
  const natoIdx = h % NATO.length;
  const suffixIdx = (h >> 5) % SUFFIXES.length;
  let candidate = `${NATO[natoIdx]} ${SUFFIXES[suffixIdx]}`;

  // Collision avoidance — append a numeric suffix if needed
  let n = 2;
  const taken = new Set(tenantNameMap.values());
  while (taken.has(candidate)) {
    candidate = `${NATO[natoIdx]} ${SUFFIXES[suffixIdx]} ${n++}`;
  }

  tenantNameMap.set(real, candidate);
  return candidate;
}

// ---------------------------------------------------------------------------
// Report-version generation
// ---------------------------------------------------------------------------

const versionMap = new Map<string, string>();

function fakeReportVersion(key: string): string {
  const cached = versionMap.get(key);
  if (cached) return cached;

  const h = hash(key);
  const major = 1 + (h % 3);          // 1, 2, or 3
  const minor = 1 + ((h >> 4) % 5);   // 1, 2, 3, 4, or 5
  const v = `${major}.${minor}`;
  versionMap.set(key, v);
  return v;
}

// ---------------------------------------------------------------------------
// Sanitizer entrypoint — walks any API response and replaces sensitive fields
// ---------------------------------------------------------------------------

const TENANT_FIELDS = new Set(['tenantName', 'TenantName', 'tenant_name']);
const VERSION_FIELDS = new Set(['reportVersion', 'ReportVersion', 'report_version']);
const ID_HINTS = ['buildingReportId', 'reportId', 'id'];

/**
 * Returns a sanitized copy of `data` if demo mode is on, otherwise returns
 * the input unchanged.
 *
 * Cycles in objects are not expected (API responses are tree-shaped JSON)
 * but the walker doesn't track visited nodes — keep that in mind if usage
 * ever expands beyond serialized API responses.
 */
export function sanitizeForDemo<T>(data: T): T {
  if (!isDemoMode()) return data;
  return walk(data) as T;
}

function walk(node: unknown): unknown {
  if (node === null || node === undefined) return node;
  if (Array.isArray(node)) return node.map(walk);
  if (typeof node !== 'object') return node;

  const obj = node as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (TENANT_FIELDS.has(key) && typeof value === 'string' && value.length > 0) {
      out[key] = fakeTenantName(value);
    } else if (VERSION_FIELDS.has(key)) {
      out[key] = transformVersion(obj, value);
    } else {
      out[key] = walk(value);
    }
  }

  return out;
}

function transformVersion(parent: Record<string, unknown>, value: unknown): unknown {
  // Empty / null version → derive a stable X.Y from the parent's ID
  if (value === null || value === undefined || value === '') {
    const idKey = ID_HINTS.find((k) => parent[k] != null);
    const seed = idKey ? String(parent[idKey]) : Math.random().toString();
    return fakeReportVersion(seed);
  }

  // Plain integer (e.g. 3) or numeric string (e.g. "3") → X.Y format
  if (typeof value === 'number') {
    return fakeReportVersion(`v${value}`);
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return fakeReportVersion(`v${value}`);
  }

  // Already X.Y or other custom format — leave alone
  return value;
}

// ---------------------------------------------------------------------------
// Test helper — clears caches between unit-test runs
// ---------------------------------------------------------------------------

export function _resetDemoSanitizerCache(): void {
  tenantNameMap.clear();
  versionMap.clear();
}
